<template>
    <!-- PRODUCT-26110: these guards ask whether a slot RENDERS something, not merely
         whether it was passed. Vue 3's `$slots.x` is a function that exists as soon as
         the parent mentions the slot, so a bare `$slots.icon` was true where Vue 2 said
         false -- reserving icon padding and emitting an empty icon container on pages
         that pass no icon (Branding, Support Contact). See utils/slotContent. -->
    <div
        class="FormInput"
        :class="{
            'FormInput--with-icon': hasIcon,
            'FormInput--error': invalid,
        }"
    >
        <div class="FormInput__inputRow">
            <div class="FormInput__inputContainer">
                <div class="FormInput__input">
                    <slot />
                </div>
                <div v-if="hasIcon" class="FormInput__icon">
                    <slot name="icon" />
                </div>
            </div>
            <div v-if="forceActionSlot || hasActionButtons" class="FormInput__actionContainer">
                <slot name="actionButtons" />
            </div>
        </div>

        <div v-if="forceErrorSlot || hasErrorMessage" class="FormInput__errorMessage">
            <div v-if="errorIcon" class="FormInput__errorIcon">
                <FontAwesomeIcon v-if="hasErrorMessage" :icon="errorIcon" />
            </div>
            <div class="FormInput__errorContent">
                <slot name="errorMessage" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "sass:color";

$form-input-vertical-padding: 10px;
$form-input-horizontal-padding: 15px;
$input-types-selector: (
    'input[type="text"],'
    + 'input[type="email"],'
    + 'input[type="password"],'
    + 'input[type="number"],'
    + 'input[type="tel"],'
    + 'input[type="url"]'
);

.FormInput {
    position: relative;
    display: block;
    width: 100%;

    // Base input styles
    &__input {
        #{$input-types-selector},
        textarea,
        select {
            width: 100%;
            display: block;
            padding: $form-input-vertical-padding $form-input-horizontal-padding;
            border: 1px solid #c9d1de;
            font: inherit;
            font-size: 1em;
            line-height: 1.25em;
            color: color.mix(#778ea1, #000, 50%);
            background: #fff;
            outline: none;
            border-radius: 3px;

            &:focus:not([disabled]) {
                border-width: 1px;
                border-color: #2ab496;
            }

            &[disabled] {
                &, &:focus, &:hover {
                    border-color: color.mix(#c9d1de, #f7f9fc, 50%);
                    color: rgba(0, 0, 0, 0.5);
                    cursor: not-allowed;
                }
            }

            &::placeholder {
                color: #b0bfcb;
            }
        }

        .Dropdown__container {
            background-color: #fff;
            border-radius: 3px;
            color: color.mix(#778ea1, #000, 50%);
        }

        .Dropdown__arrow {
            color: #778ea1;
        }
    }

    // Layout structure
    &__inputRow {
        display: flex;
        width: 100%;
    }

    &__inputContainer {
        position: relative;
        flex: 1 0;
        min-width: 0; // Prevents flex item from overflowing
    }

    // Icon styles
    &--with-icon &__input {
        #{$input-types-selector},
        textarea,
        select {
            appearance: none;
            padding-right: calc(#{$form-input-horizontal-padding * 2} + 1em);

            &::placeholder, & .empty {
                color: #778ea1;
            }
        }
    }

    &__icon {
        position: absolute;
        top: 50%;
        right: $form-input-horizontal-padding;
        font-size: 0.8em;
        width: 1em;
        text-align: center;
        transform: translateY(-50%);
        color: #778ea1;

        > img {
            height: 1em;
            display: block;
            margin: 0 auto;
        }
    }

    // Action container
    &__actionContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 5px;
        flex-shrink: 0;
        visibility: visible; // Default visible for when slots exist
    }

    // Error states
    &--error &__input {
        #{$input-types-selector},
        textarea,
        select,
        .Dropdown__container {
            &, &:not([disabled]) {
                border-width: 1px;
                border-color: #f05b46;
            }
        }
    }

    &__errorMessage {
        display: flex;
        color: #f05b46;
        padding: 2px 5px;
        font-size: 0.85rem;
        align-items: flex-start;
        margin-top: 4px;
        width: auto;
    }

    &__errorIcon {
        flex-shrink: 0;
        margin-right: 5px;
    }

    &__errorContent {
        flex: 1;
        min-width: 0; // Ensures text truncation works if needed
    }

    // Handle the forced action space case
    &--hasReservedActionSpace {
        .FormInput__actionContainer {
            visibility: hidden; // Hide but preserve space
            min-width: 32px; // Force a minimum width that matches common button sizes
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue } from 'vue-property-decorator';

    import { faExclamationTriangle, IconDefinition } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { hasSlotContent } from '@evidentid/vue-commons/utils/slotContent';

    @Component({
        components: { FontAwesomeIcon },
    })
    export default class FormInput extends Vue {
        @Prop({ type: Boolean, default: false })
        private invalid!: boolean;

        @Prop({ type: Boolean, default: false })
        protected forceErrorSlot!: boolean;

        @Prop({ type: Boolean, default: false })
        protected forceActionSlot!: boolean;

        @Prop({ default: () => faExclamationTriangle })
        protected errorIcon!: IconDefinition;

        protected get hasIcon(): boolean {
            return hasSlotContent(this.$slots.icon);
        }

        protected get hasActionButtons(): boolean {
            return hasSlotContent(this.$slots.actionButtons);
        }

        protected get hasErrorMessage(): boolean {
            return hasSlotContent(this.$slots.errorMessage);
        }
    }
</script>
